import { isPlatformBrowser } from '@angular/common';
import { BreakpointObserver } from '@angular/cdk/layout';
import {
  afterNextRender,
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  PLATFORM_ID,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import gsap from 'gsap';

@Component({
  selector: 'app-angular-monolith',
  standalone: true,
  templateUrl: './angular-monolith.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AngularMonolithComponent {
  readonly isMobile = signal(true);

  readonly containerRef =
    viewChild.required<ElementRef<HTMLElement>>('container');
  readonly monolithRef = viewChild.required<ElementRef<SVGElement>>('monolith');
  readonly orbitalGroupRef =
    viewChild.required<ElementRef<HTMLElement>>('orbitalGroup');
  readonly badgeBrRef = viewChild<ElementRef<HTMLElement>>('badgeBr');

  private readonly platformId = inject(PLATFORM_ID);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly destroyRef = inject(DestroyRef);
  private readonly mobileMediaQuery = '(max-width: 767px)';

  constructor() {
    afterRenderEffect((onCleanup) => {
      if (!isPlatformBrowser(this.platformId)) return;

      const badgeBr = this.badgeBrRef();
      if (!badgeBr) return;

      const beaconAnimation = gsap.to(badgeBr.nativeElement, {
        boxShadow: '0 0 20px 5px rgba(250, 204, 21, 0.5)',
        duration: 1.5,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
      });

      onCleanup(() => beaconAnimation.kill());
    });

    afterNextRender(() => {
      if (!isPlatformBrowser(this.platformId)) return;

      this.breakpointObserver
        .observe(this.mobileMediaQuery)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(({ matches }) => this.isMobile.set(matches));

      this.initAnimations();
    });
  }

  private initAnimations() {
    // 3.4 Configure GSAP matchMedia for mobile fallback
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mm = gsap.matchMedia();

      mm.add('(min-width: 769px)', () => {
        // 3.3 Mouse Parallax Tilt 3D
        const container = this.containerRef().nativeElement;

        const onMouseMove = (e: MouseEvent) => {
          const rect = container.getBoundingClientRect();
          const x = e.clientX - rect.left - rect.width / 2;
          const y = e.clientY - rect.top - rect.height / 2;

          gsap.to(this.monolithRef().nativeElement, {
            rotationY: (x / rect.width) * 30,
            rotationX: -(y / rect.height) * 30,
            duration: 1,
            ease: 'power2.out',
          });

          gsap.to(this.orbitalGroupRef().nativeElement, {
            x: (x / rect.width) * 40,
            y: (y / rect.height) * 40,
            duration: 1.5,
            ease: 'power2.out',
          });
        };

        const onMouseLeave = () => {
          gsap.to(
            [
              this.monolithRef().nativeElement,
              this.orbitalGroupRef().nativeElement,
            ],
            {
              rotationY: 0,
              rotationX: 0,
              x: 0,
              y: 0,
              duration: 1.5,
              ease: 'power2.out',
            },
          );
        };

        container.addEventListener('mousemove', onMouseMove);
        container.addEventListener('mouseleave', onMouseLeave);

        return () => {
          container.removeEventListener('mousemove', onMouseMove);
          container.removeEventListener('mouseleave', onMouseLeave);
        };
      });
    }

    // 3.2 Continuous sine wave animation for orbital elements
    gsap.to(this.monolithRef().nativeElement, {
      y: -15,
      duration: 3,
      repeat: -1,
      yoyo: true,
      ease: 'sine.inOut',
    });

    gsap.to(this.orbitalGroupRef().nativeElement, {
      y: 10,
      rotationZ: 2,
      duration: 4,
      repeat: -1,
      yoyo: true,
      ease: 'sine.inOut',
    });
  }
}
